import { Table, Text } from "@ttc3k/trekker";
import {
  EnumGuestTierType,
  EnumPriceTierPriceType,
  EnumProductType,
  PriceTier,
  Product,
} from "gql/generated";
import pluralize from "pluralize";
import {
  getAppropriateGuestTiers,
  type CartItem as CartItemType,
} from "widgets/booker/contexts";
import { Flex } from "styled-system/jsx";
import { getTotalGuestCount } from "../utils/getTotalGuestCount";
import { differenceInCalendarDays, format, isValid, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { formatPrice } from "shared/utils";
import { useTranslation } from "react-i18next";
import { getDiscountedTierPrice } from "../utils";
import {
  buildSummaryLines,
  storedTierLinesToSummaryLines,
  merchItemToSummaryLines,
} from "./buildSummaryLines";
import type { MultilingualLangKey } from "shared/types";

type CartItemProps = {
  item: CartItemType;
  isReservationPage?: boolean;
  mainProduct?: Product;
  selectedTierId?: string | null;
  language?: MultilingualLangKey;
};

export const CartItem = ({
  item,
  isReservationPage = false,
  mainProduct,
  selectedTierId,
  language = "en",
}: CartItemProps) => {
  const { t } = useTranslation("booker");
  const totalGuestCount = getTotalGuestCount(item.guestsPerTier);

  const eventSessionCount = item.sessionIds?.length ?? (item.sessionId ? 1 : 0);
  const parsedStartDate = item.startDateISO
    ? parseISO(`${item.startDateISO}T00:00:00`)
    : null;
  const eventDateLabel =
    parsedStartDate && isValid(parsedStartDate)
      ? format(parsedStartDate, "MMM d, yyyy")
      : "";
  const eventDetail =
    eventSessionCount > 1
      ? t("CHECKOUT.SUMMARY.SESSION", { count: eventSessionCount })
      : eventDateLabel;
  const numberOfNights =
    item.startDateISO && item.endDateISO
      ? differenceInCalendarDays(
          toZonedTime(item.endDateISO, "UTC"),
          toZonedTime(item.startDateISO, "UTC"),
        )
      : 0;
  const isMainProductAndPerGuestPricing =
    mainProduct && mainProduct.pricing
      ? mainProduct.pricing.tiers.some(
          tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
        )
      : false;

  const guestTiers = getAppropriateGuestTiers(
    item.guestsPerTier,
    mainProduct?.pricing,
    isMainProductAndPerGuestPricing,
    true, // Include full tier data in the return
  );

  if (item.productType === EnumProductType.Accommodation && !numberOfNights) {
    return null;
  }

  // Post-purchase (receipts/PDFs): render only the lines frozen on the
  // order — never live product config. Records without stored lines (created
  // before tier-line snapshots) fall back to the single product-name row
  // below. Live computation is reserved for the pre-purchase checkout cart.
  const optionLines = isReservationPage
    ? (storedTierLinesToSummaryLines(item, item.storedTierLines, {
        language,
        eventDetail: eventDetail || undefined,
      }) ?? merchItemToSummaryLines(item))
    : buildSummaryLines(item, mainProduct, {
        selectedTierId,
        language,
        eventDetail: eventDetail || undefined,
      });

  if (optionLines) {
    return (
      <>
        {optionLines.map(line => (
          <Table.Row key={line.key}>
            <Table.Header paddingRight={"150"}>
              <Flex flexDir={"column"}>
                <Text color={"text.dark"}>{line.label}</Text>
                {line.detail ? (
                  <Text color={"text.mid"} visual={"smallRegular"}>
                    {line.detail}
                  </Text>
                ) : null}
              </Flex>
            </Table.Header>
            <Table.Cell whiteSpace="nowrap">
              {line.amount === 0
                ? t("HEADER.FREE")
                : `$${formatPrice(line.amount)}`}
            </Table.Cell>
          </Table.Row>
        ))}
      </>
    );
  }

  return (
    <Table.Row key={item.productName}>
      <Table.Header paddingRight={"150"}>
        <Flex flexDir={"column"}>
          <Text color={"text.dark"}>
            {item.productName}
            {item.quantity > 1 &&
              item.productType !== EnumProductType.Accommodation &&
              ` × ${item.quantity}`}
            {item.productType === EnumProductType.Accommodation &&
              isReservationPage &&
              !isMainProductAndPerGuestPricing &&
              ` | ${t("CHECKOUT.SUMMARY.NIGHT", { count: numberOfNights })}`}
          </Text>
          {item.productType === EnumProductType.Event && eventDetail ? (
            <Text color={"text.mid"} visual={"smallRegular"}>
              {eventDetail}
            </Text>
          ) : null}
          {item.productType === EnumProductType.Accommodation && (
            <>
              {!isReservationPage && !isMainProductAndPerGuestPricing && (
                <Text color={"text.mid"}>
                  {t("CHECKOUT.SUMMARY.NIGHT", { count: numberOfNights })}
                  &nbsp;•&nbsp;
                  {t("CHECKOUT.SUMMARY.GUEST", { count: totalGuestCount })}
                </Text>
              )}
              {/* Per guest pricing - we want this data to display on the reservation page & in booking widget */}
              {isMainProductAndPerGuestPricing && (
                <>
                  <Text color={"text.mid"} visual={"smallRegular"}>
                    {t("CHECKOUT.SUMMARY.NIGHT", { count: numberOfNights })}
                  </Text>
                  {guestTiers
                    .filter(tier => tier.count > 0)
                    .map(tier => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const fullTier = (tier as any).fullTier as PriceTier;
                      if (!fullTier) return null;

                      const tierPriceWithDiscount =
                        getDiscountedTierPrice(fullTier);

                      return (
                        <Text
                          key={tier.tierId}
                          color={"text.mid"}
                          visual={"smallRegular"}
                        >
                          {pluralize(
                            t(
                              "GUESTS_PAGE.GUEST_TYPES." + fullTier.type ===
                                EnumGuestTierType.General
                                ? "GUEST"
                                : fullTier.type,
                            ),
                            tier.count,
                            true,
                          )}
                          &nbsp;•&nbsp;${formatPrice(tierPriceWithDiscount)}
                          &nbsp;
                          {t("CHECKOUT.SUMMARY.EACH_PER_NIGHT")}
                        </Text>
                      );
                    })}
                </>
              )}
            </>
          )}
        </Flex>
      </Table.Header>
      <Table.Cell whiteSpace="nowrap">
        {item.total === 0 ? t("HEADER.FREE") : `$${formatPrice(item.total)}`}
      </Table.Cell>
    </Table.Row>
  );
};
